import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AreaMixedChart from '../../components/ApexCharts/AreaMixedChart';
import MultipleColumnsChart from '../../components/ApexCharts/MultipleColumnsChart';
import Button from '../../components/Button';
import CalloutCard from '../../components/CalloutCard/CalloutCard';
import GenericCard from '../../components/GenericCards';
import NumberInput from '../../components/NumberInput/NumberInput';
import RangeSlider from '../../components/RangeSlider/RangeSlider';
import Tabs from '../../components/Tabs/Tabs';
import { PENSION_ROUTE, RETIRE_ROUTE } from '../../routes/types';
import { getAllProfiles } from '../../services/profiles';
import {
  defaultSliderInitialInputs,
  fixedAssumptionsData,
} from '../../services/retirementPlanning';
import {
  convertPercentageToDecimal,
  generateIntegerRange,
} from '../../utils/utils';
import YourAssumptions from '../widgets/RetirementPlanning/YourAssumptions';

const getTabsData = ({
  iRetAge,
  iIncRep,
  iEECont,
  iROR,
  cAge,
  cHSA,
  c401k,
  cPS,
  cPay,
  SScal,
  Inf,
}: {
  iRetAge: number;
  iIncRep: number;
  iEECont: number;
  iROR: number;
  cAge: number;
  cHSA: number;
  c401k: number;
  cPS: number;
  cPay: number;
  SScal: number;
  Inf: number;
}) => {
  const ageRange = generateIntegerRange(iRetAge, 100);
  const iIncRepDecimal = convertPercentageToDecimal(iIncRep);
  const iEEContDecimal = convertPercentageToDecimal(iEECont);
  const iRORDecimal = convertPercentageToDecimal(iROR);

  return [
    {
      name: 'Income',
      content: (
        <div className="overflow-auto pt-11">
          <div style={{ minWidth: '520px' }}>
            <MultipleColumnsChart
              cAge={cAge}
              ageRange={ageRange}
              iROR={iRORDecimal}
              iEECont={iEEContDecimal}
              iRetAge={iRetAge}
              cHSA={cHSA}
              cPS={cPS}
              cPay={cPay}
              c401k={c401k}
              SScal={SScal}
              iIncRep={iIncRepDecimal}
              Inf={Inf}
            />
          </div>
        </div>
      ),
    },

    {
      name: 'Account Balance',
      content: (
        <AreaMixedChart
          cAge={cAge}
          ageRange={ageRange}
          iROR={iRORDecimal}
          iEECont={iEEContDecimal}
          iRetAge={iRetAge}
          cHSA={cHSA}
          cPS={cPS}
          cPay={cPay}
          c401k={c401k}
          SScal={SScal}
          iIncRep={iIncRepDecimal}
          Inf={Inf}
        />
      ),
    },
    {
      name: 'Your Assumptions',
      content: (
        <YourAssumptions
          iROR={iROR}
          iEECont={iEECont}
          iRetAge={iRetAge}
          iIncRep={iIncRep}
        />
      ),
    },
  ];
};

const RetirementPlanning = () => {
  const navigate = useNavigate();
  const [mainUser] = getAllProfiles().data.filter(
    (profile) => profile.relationship === 'self'
  );

  const [iRetAge, setIRetAge] = useState<number>(
    defaultSliderInitialInputs.iRetAge
  );
  const [iIncRep, setIIncRep] = useState<number>(
    defaultSliderInitialInputs.iIncRep
  );
  const [iEECont, setIIEECont] = useState<number>(
    defaultSliderInitialInputs.iEECont
  );
  const [iROR, setIROR] = useState<number>(defaultSliderInitialInputs.iROR);
  const cAge = mainUser.age;
  const cHSA = fixedAssumptionsData.cHSA;
  const c401k = fixedAssumptionsData.c401k;
  const cPS = fixedAssumptionsData.cPS;
  const cPay = fixedAssumptionsData.cPay;
  const SScal = fixedAssumptionsData.SScal;
  const Inf = fixedAssumptionsData.Inf;
  const [showMore, setShowMore] = React.useState<boolean>(false);
  const [annualSalary, setAnnualSalary] = React.useState<
    number | null | undefined
  >(null);
  const [personalSavings, setPersonalSavings] = React.useState<
    number | null | undefined
  >(null);
  const [outsideAnnuities, setOutsideAnnuities] = React.useState<
    number | null | undefined
  >(null);
  const [socialSecurityOverride, setSocialSecurityOverride] = React.useState<
    number | null | undefined
  >(null);

  const handleChangeIRetAge = (value: number) => {
    setIRetAge(value);
  };

  const handleChangeIIncRep = (value: number) => {
    setIIncRep(value);
  };

  const buttons = useMemo(
    () => [
      <Button
        variant="secondary-outline"
        rightIcon={light('scale-balanced')}
        className="callout-button"
        size="sm"
        onClick={() => {
          navigate(PENSION_ROUTE.replace(':tab', 'new'));
        }}
      >
        Pension Estimate
      </Button>,

      <Button
        variant="secondary-outline"
        rightIcon={light('person-cane')}
        className="callout-button"
        size="sm"
        onClick={() => {
          navigate(RETIRE_ROUTE.replace(':option', 'start'));
        }}
      >
        Retire Online
      </Button>,
    ],
    []
  );

  const handleChangeIEECont = (value: number) => {
    setIIEECont(value);
  };

  const handleChangeIROR = (value: number) => {
    setIROR(value);
  };

  const toggleShowMore = (e: any) => {
    setShowMore(!showMore);
  };

  const handleAnnualSalaryChange = (value: number | null | undefined) => {
    setAnnualSalary(value);
  };

  const handlePersonalSavingsChange = (value: number | null | undefined) => {
    setPersonalSavings(value);
  };

  const handleOutsideAnnuitiesChange = (value: number | null | undefined) => {
    setOutsideAnnuities(value);
  };

  const handleSocialSecurityOverrideChange = (
    value: number | null | undefined
  ) => {
    setSocialSecurityOverride(value);
  };

  const handleRetirementGoalsSubmit = (data: any) => {
    console.log('submit', data);
  };

  const iRetAgeSliderMinValue = Math.max(55, cAge + 1);

  return (
    <Container className="cnd-custom-container-large px-lg-9 px-xl-0 mt-lg-11">
      <Container className="px-7 px-lg-0 mb-11 mb-lg-12 mt-lg-12">
        <Row>
          <Col className="col-lg-5 mt-lg-12">
            <h2 className="text-sm-24-lg-32 mb-7">Retirement Planning</h2>
            <div className="text-fs-6 fw-light">
              Based upon your current inputs and assumptions.
            </div>
          </Col>
        </Row>
        <Row className="px-lg-0 pt-12">
          <Col className="col-lg-5 col-12">
            <Row>
              <Col>
                <GenericCard variant={'white'}>
                  <Row>
                    <Col>
                      <Row>
                        {' '}
                        <h6>Retirement Goals and Assumptions</h6>
                      </Row>
                    </Col>
                    <Row className="pt-12">
                      <Col>
                        <RangeSlider
                          min={iRetAgeSliderMinValue}
                          max={70}
                          step={1}
                          tooltipID="tooltip1"
                          inputID="input1"
                          label="What age do you want to stop working?"
                          onSliderChange={handleChangeIRetAge}
                          showRangeLabel={true}
                          defaultValue={iRetAge}
                        />
                      </Col>
                    </Row>
                    <Row className="pt-12">
                      <Col>
                        <RangeSlider
                          min={50}
                          max={120}
                          step={1}
                          tooltipID="tooltip2"
                          inputID="input2"
                          label="What % of your income will you need at retirement?"
                          showRangeLabel={true}
                          showPercentSymbol={true}
                          onSliderChange={handleChangeIIncRep}
                          defaultValue={iIncRep}
                        />
                      </Col>
                    </Row>
                    <Row className="pt-12">
                      <Col>
                        <RangeSlider
                          min={0}
                          max={30}
                          step={1}
                          tooltipID="tooltip3"
                          inputID="input3"
                          label="How much can you afford to contribute? "
                          showRangeLabel={true}
                          showPercentSymbol={true}
                          onSliderChange={handleChangeIEECont}
                          defaultValue={iEECont}
                        />
                      </Col>
                    </Row>
                    <Row className="pt-12">
                      <Col>
                        <RangeSlider
                          min={0}
                          max={10}
                          step={1}
                          tooltipID="tooltip4"
                          inputID="input4"
                          label="What is your expected rate of return?"
                          showRangeLabel={true}
                          showPercentSymbol={true}
                          onSliderChange={handleChangeIROR}
                          defaultValue={iROR}
                        />
                      </Col>
                    </Row>
                  </Row>
                  {showMore && (
                    <Row>
                      <Row>
                        <Col style={{ maxWidth: 300 }}>
                          <NumberInput
                            icon={light('dollar')}
                            placeholder={'0'}
                            label={'Annual Salary'}
                            className="pt-11"
                            value={annualSalary || 0}
                            minValue={'0'}
                            onChange={handleAnnualSalaryChange}
                          />
                        </Col>
                      </Row>
                      <Row className="pt-11">
                        <Col>
                          <RangeSlider
                            min={0}
                            max={100}
                            step={1}
                            tooltipID="tooltip5"
                            inputID="input5"
                            label="Bonus Target"
                            showRangeLabel={true}
                            showPercentSymbol={true}
                          />
                        </Col>
                      </Row>
                      <Row className="pt-11">
                        <Col>
                          <RangeSlider
                            min={0}
                            max={100}
                            step={1}
                            tooltipID="tooltip6"
                            inputID="input6"
                            label="Annual Salary Increase"
                            showRangeLabel={true}
                            showPercentSymbol={true}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col style={{ maxWidth: 300 }}>
                          <NumberInput
                            icon={light('dollar')}
                            placeholder={'0'}
                            label={'Personal Savings'}
                            className="pt-11"
                            value={personalSavings || 0}
                            minValue={'0'}
                            onChange={handlePersonalSavingsChange}
                          />
                        </Col>
                      </Row>
                      <Row className="pt-11">
                        <Col>
                          <RangeSlider
                            min={0}
                            max={100}
                            step={1}
                            tooltipID="tooltip7"
                            inputID="input7"
                            label="Personal Savings Contribution"
                            showRangeLabel={true}
                            showPercentSymbol={true}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col style={{ maxWidth: 300 }}>
                          <NumberInput
                            icon={light('dollar')}
                            placeholder={'0'}
                            label={'Outside Annuities'}
                            className="pt-11"
                            value={outsideAnnuities || 0}
                            minValue={'0'}
                            onChange={handleOutsideAnnuitiesChange}
                          />
                        </Col>
                      </Row>
                      <Row className="pt-11">
                        <Col>
                          <RangeSlider
                            min={0}
                            max={100}
                            step={1}
                            tooltipID="tooltip8"
                            inputID="input8"
                            label="Social Security Retirement Age"
                            showRangeLabel={true}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col style={{ maxWidth: 300 }}>
                          <NumberInput
                            icon={light('dollar')}
                            placeholder={'0'}
                            label={'Social Security Override'}
                            className="pt-11"
                            value={socialSecurityOverride || 0}
                            minValue={'0'}
                            onChange={handleSocialSecurityOverrideChange}
                          />
                        </Col>
                      </Row>
                    </Row>
                  )}
                  <div className="pt-12">
                    <section className="d-flex flex-column flex-lg-row justify-content-between">
                      {' '}
                      {showMore ? (
                        <Button
                          variant="primary-outline"
                          rightIcon={light('chevron-up')}
                          size="sm"
                          onClick={toggleShowMore}
                        >
                          Hide More Inputs and Assumptions
                        </Button>
                      ) : (
                        <Button
                          variant="primary-outline"
                          className="text-nowrap "
                          rightIcon={light('chevron-down')}
                          size="sm"
                          onClick={toggleShowMore}
                        >
                          Show More Inputs and Assumptions
                        </Button>
                      )}
                      <Button
                        variant="primary"
                        className="text-nowrap mt-7 mt-lg-0"
                        size="sm"
                      >
                        Save inputs
                      </Button>
                    </section>
                  </div>
                </GenericCard>
              </Col>
            </Row>
          </Col>
          <Col className="col-lg-7 pt-lg-0 pt-10 px-10">
            <Row>
              <GenericCard variant={'white'}>
                <Tabs
                  tabs={getTabsData({
                    iRetAge,
                    iIncRep,
                    iEECont,
                    iROR,
                    cAge,
                    cHSA,
                    c401k,
                    cPS,
                    cPay,
                    SScal,
                    Inf,
                  })}
                />
              </GenericCard>
            </Row>
          </Col>
        </Row>
        <Row className="pt-lg-12 pt-10">
          <Col>
            <CalloutCard
              cardIcon={light('screwdriver-wrench')}
              buttons={buttons}
            >
              <Row className="content-row">
                <p className="bolder-text">What’s Next?</p>
                <span>
                  Now that you've completed your Retirement Planning, here are a
                  few other items you might want to consider
                </span>
              </Row>
            </CalloutCard>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};
export default RetirementPlanning;
